'use client';

import React from 'react';
import classNames from 'classnames';
import { useTranslate } from '@tolgee/react';
import {
  Caption,
  EmployeeBaseCard,
  ChevronRightIcon,
  SubtleButtonLink,
  Text,
} from '@/design-system';
import {
  CardCategoryLabel,
  CardCategory,
} from '@/design-system/components/Basic/Card/CardCategoryLabel';
import type { MaternityNotificationResponseDto } from '@/types/Api';
import { AssessmentStatus } from '@/types/Api';
import { formatDateToDe } from '@/utils/helpers';

const STATUS_I18N: Record<AssessmentStatus, string> = {
  [AssessmentStatus.Pending]: 'maternityRiskAssessment.assessment.status.pending',
  [AssessmentStatus.NoMeasuresNecessary]:
    'maternityRiskAssessment.assessment.status.noMeasuresNecessary',
  [AssessmentStatus.MeasuresNecessary]:
    'maternityRiskAssessment.assessment.status.measuresNecessary',
};

export interface MaternityCardProps {
  notification: MaternityNotificationResponseDto;
  /** Assessment status for this notification (AssessmentStatus from API) */
  assessmentStatus?: AssessmentStatus | null;
  /** When assessment is completed, pass completedAt to show "Assessment completed on" date */
  completedAt?: string | null;
  /** Optional href. If not provided, the card is not clickable. */
  to?: string;
  className?: string;
  /** Optional label at bottom. Defaults to "Risk Assessment Maternity" (ohs-hub.maternity.title) */
  label?: string;
  /** Optional. Called when the right chevron is clicked. Stops propagation so card link is not followed. */
  onAction?: () => void;
  /** Optional. Called when "Start assessment" is clicked (pending only). If provided, the button is an action instead of a link. Stops propagation. */
  onStartAssessment?: () => void;
}

export const MaternityCard: React.FC<MaternityCardProps> = ({
  notification,
  assessmentStatus = AssessmentStatus.Pending,
  completedAt,
  to,
  className,
  label,
  onAction,
  onStartAssessment,
}) => {
  const { t } = useTranslate();
  const notificationDateFormatted = notification.notificationDate
    ? formatDateToDe(notification.notificationDate)
    : null;
  const dueDateFormatted = notification.expectedDueDate
    ? formatDateToDe(notification.expectedDueDate)
    : null;
  const completedAtFormatted = completedAt ? formatDateToDe(completedAt) : null;
  const statusKey = assessmentStatus
    ? STATUS_I18N[assessmentStatus]
    : STATUS_I18N[AssessmentStatus.Pending];
  const statusLabel = t(statusKey);

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAction?.();
  };

  const handleStartAssessmentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onStartAssessment?.();
  };

  return (
    <EmployeeBaseCard to={to} className={className}>
      <div className="flex flex-col gap-6 flex-1 min-h-0">
        {/* Header row: status tag + chevron */}
        <div className="flex justify-end items-center gap-2 shrink-0 pl-4 pr-2">
          <div
            className={classNames(
              'flex justify-center items-center gap-2.5 py-1 px-2 rounded-ds-2',
              assessmentStatus === AssessmentStatus.Pending
                ? 'bg-ds-bg-feedback-soft-info'
                : 'bg-ds-bg-surface-soft-3',
            )}
          >
            <Text size="s" weight="semibold" className="text-ds-text-1">
              {statusLabel}
            </Text>
          </div>

          {onAction ? (
            <button
              type="button"
              onClick={handleChevronClick}
              aria-label={t('ohs-hub.maternity.title')}
            >
              <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
            </button>
          ) : (
            <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
          )}
        </div>

        {/* Content rows: symmetric px-4 */}
        <div className="flex flex-col gap-6 min-w-0 px-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-0">
            {dueDateFormatted != null && (
              <div className="flex flex-col gap-2 min-w-0">
                <Caption weight="medium" className="text-ds-text-3">
                  {t('maternityRiskAssessment.statusCard.dueDate')}
                </Caption>
                <Text size="s" weight="semibold" className="text-ds-text-1">
                  {dueDateFormatted}
                </Text>
              </div>
            )}
            {notificationDateFormatted != null && (
              <div className="flex flex-col gap-2 min-w-0">
                <Caption weight="medium" className="text-ds-text-3">
                  {t('maternityRiskAssessment.statusCard.notificationDate')}
                </Caption>
                <Text size="s" weight="semibold" className="text-ds-text-1">
                  {notificationDateFormatted}
                </Text>
              </div>
            )}
          </div>
          {completedAtFormatted != null && (
            <div className="flex flex-col gap-2 min-w-fit">
              <Caption weight="medium" className="text-ds-text-3">
                {t('maternityRiskAssessment.statusCard.assessmentCompletedOn')}
              </Caption>
              <Text size="s" weight="semibold" className="text-ds-text-1">
                {completedAtFormatted}
              </Text>
            </div>
          )}
          {assessmentStatus === AssessmentStatus.Pending && onStartAssessment && (
            <div className="flex justify-end">
              <SubtleButtonLink onClick={handleStartAssessmentClick}>
                {t('maternityRiskAssessment.statusCard.startAssessment')}
              </SubtleButtonLink>
            </div>
          )}
        </div>
      </div>
      <CardCategoryLabel category={CardCategory.MATERNITY}>
        {label ?? t('ohs-hub.employeeDetails.maternityCard.label')}
      </CardCategoryLabel>
    </EmployeeBaseCard>
  );
};
